import {IssueGroupingKey, usePlantStoppages, useStoppageCodeStats} from '@domain/stoppages'
import {DataContentWrapper} from '@hconnect/common/containers'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Grid} from '@mui/material'
import moment from 'moment-timezone'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, useNavigate} from 'react-router'

import {StatusPageHeader, DowntimeFilters, StoppageStatsTable, TopIssues} from '../components'
import {
  DOWNTIME_STATISTICS_PATH,
  DOWNTIME_STOPPAGE_DETAILS_PATH,
  DowntimeAnalyticsEventName,
  FEATURE_NAMESPACE
} from '../consts'
import {
  useDowntimeFiltersState,
  usePlantTimeRange,
  useSortedStoppages,
  useTimeRangeParam,
  useTrackDowntimeDashboardAnalytics
} from '../hooks'
import {useDowntimePlantId, useStoppagesStatsTimeRange} from '../providers'
import {calculateEndDate, getCombinedDuration, getTimeStep} from '../utils'

export const DowntimeStoppagesStats = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const plantId = useDowntimePlantId()
  const [, setTimeRange] = useTimeRangeParam()
  const navigate = useNavigate()
  const trackAnalyticsEvent = useTrackDowntimeDashboardAnalytics(plantId)
  const timezone = useUpmPlantTimezone(plantId)

  const {
    data: stoppageConfig,
    isInitialLoading,
    isError,
    refetch,
    isRefetching
  } = usePlantStoppages(plantId)

  const {
    failureStoppage,
    setFailureStoppage,
    mainEquipments,
    setMainEquipments,
    equipmentNumbers,
    setEquipmentNumbers,
    stoppageCodes,
    setStoppageCodes,
    stoppageDuration,
    setStoppageDuration
  } = useDowntimeFiltersState()

  const timeRange = usePlantTimeRange()
  const timeStep = getTimeStep(timeRange, timezone)

  const selectedMainEquipmentIds = mainEquipments?.map((eq) => eq.id)
  const selectedEquipmentIds = equipmentNumbers?.map((eq) => eq.id)

  const {
    topIssuesSortingKey,
    setTopIssuesSortingKey,
    topIssuesGroupingKey,
    setTopIssuesGroupingKey,
    sortedStoppages,
    aggregatedStoppagesQuery
  } = useSortedStoppages({
    plantId,
    groupingEventName: DowntimeAnalyticsEventName.UserSet2LevelGrouping,
    sortingEventName: DowntimeAnalyticsEventName.UserSet2LevelSorting,
    timeRange,
    selectedMainEquipments: selectedMainEquipmentIds,
    selectedStoppages: failureStoppage,
    selectedStoppageCodes: stoppageCodes,
    ...(stoppageDuration && {
      stoppageType: ['kiln'],
      durationInterval: getCombinedDuration(stoppageDuration)
    }),
    selectedEquipmentNumbers: selectedEquipmentIds,
    timeStep
  })
  const stoppageCodeStatsQuery = useStoppageCodeStats(plantId, {
    ...timeRange,
    groupBy: topIssuesGroupingKey,
    ...(selectedMainEquipmentIds && {selectedMainEquipments: selectedMainEquipmentIds}),
    ...(selectedEquipmentIds && {selectedEquipments: equipmentNumbers?.map((eq) => eq.id)}),
    ...(failureStoppage && {selectedStoppages: failureStoppage}),
    ...(stoppageCodes && {selectedStoppageCodes: stoppageCodes}),
    ...(stoppageDuration && {
      stoppageType: ['kiln'],
      durationInterval: getCombinedDuration(stoppageDuration)
    }),
    timeStep
  })
  const {setStoppagesStatsTimeRange} = useStoppagesStatsTimeRange()
  const setTimeRangeFilter = useCallback(
    (utcStartDate: string) => {
      const start = moment.utc(utcStartDate)
      const utcEndDate = calculateEndDate(timeStep, start.tz(timezone))
      const utcDateString = `[${start.toJSON()},${utcEndDate.toJSON()}]`

      if (utcDateString) {
        setTimeRange(utcDateString)
        setStoppagesStatsTimeRange(utcDateString)
      }
    },
    [setTimeRange, setStoppagesStatsTimeRange, timeStep, timezone]
  )

  return (
    <PageContainer>
      <DataContentWrapper
        showLoader={isInitialLoading}
        data={stoppageConfig}
        retryFunction={() => void refetch()}
        isError={isError}
        isRefetching={isRefetching}
      >
        <StatusPageHeader
          title={t('label.stoppages')}
          backButtonProps={{
            target: '..',
            targetName: t('actions.backToDowntime')
          }}
          filterComponent={
            <DowntimeFilters
              filteringEventName={DowntimeAnalyticsEventName.UserAdjusts2LevelFilter}
              failureState={{failureStoppage, setFailureStoppage}}
              mainEquipmentState={{mainEquipments, setMainEquipments}}
              stoppageCodesState={{stoppageCodes, setStoppageCodes}}
              equipmentNumbersState={{equipmentNumbers, setEquipmentNumbers}}
              stoppageDurationState={{stoppageDuration, setStoppageDuration}}
            />
          }
        />
        <Grid container spacing={2}>
          <Grid item md={12} xs={12}>
            <TopIssues
              timeRange={timeRange}
              stoppageConfig={stoppageConfig}
              title={t('label.downtimes')}
              issueCount={topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL ? undefined : 12}
              hideInfoIcon
              stoppageCodeStats={stoppageCodeStatsQuery.data || {}}
              showFramesLabelFromTimeRange
              onBarClick={(groupId?: string) => {
                if (groupId) {
                  if (topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL) {
                    setTimeRangeFilter(groupId)
                    const utcDateString = `[${timeRange.from.toJSON()},${timeRange.to.toJSON()}]`
                    setStoppagesStatsTimeRange(utcDateString)
                  }

                  const pathToRedirect = generatePath(DOWNTIME_STOPPAGE_DETAILS_PATH, {
                    groupType: topIssuesGroupingKey,
                    groupId
                  })
                  trackAnalyticsEvent(
                    DowntimeAnalyticsEventName.UserClicksOn2LevelStoppagesGraphBar,
                    {
                      groupId,
                      group: topIssuesGroupingKey
                    }
                  )
                  void navigate(pathToRedirect, {relative: 'path'})
                }
              }}
              topIssuesSortingKey={topIssuesSortingKey}
              onTopIssuesSortingKeyChange={setTopIssuesSortingKey}
              topIssuesGroupingKey={topIssuesGroupingKey}
              onTopIssuesGroupingKeyChange={setTopIssuesGroupingKey}
              sortedStoppages={sortedStoppages}
              aggregatedStoppagesQuery={aggregatedStoppagesQuery}
              onShowKilnStatsClick={() =>
                void navigate(`../${DOWNTIME_STATISTICS_PATH}`, {relative: 'path'})
              }
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <StoppageStatsTable
              stoppageCodeStatsQuery={stoppageCodeStatsQuery}
              stoppageConfig={stoppageConfig}
              topIssuesGroupingKey={topIssuesGroupingKey}
              sortedStoppages={sortedStoppages}
              timezone={timezone}
            />
          </Grid>
        </Grid>
      </DataContentWrapper>
    </PageContainer>
  )
}
